import * as Tone from 'tone';

// Synthesized voices. Each `trigger(time, velocity, pitch?, opts?)` schedules
// a sound at the given AudioContext time; sequencer ticks call these from
// Tone.Sequence.

export type TriggerOpts = {
  // Tonal voice only. If true, this onset is part of a run of consecutive
  // active steps — glide to the new pitch instead of restarting the envelope.
  legatoFromPrev?: boolean;
  // Tonal voice only. AudioContext time to call triggerRelease at, scheduled
  // so a held / legato run ends cleanly when the next step is off.
  releaseAt?: number;
};

// Audio engine flavor.
//   ELECTRO  — oscillator-based synth voices (the original "Synth" engine).
//   ORGANIC  — real recordings (drum one-shots from Tone.js drum-samples CDN;
//              Salamander piano for the tonal track). Adds humanization
//              (timing jitter + velocity variation) at trigger time.
//   808      — a fully-synthesized TR-808 drum kit (own track layout: 8 drum
//              voices, no tonal track). Kick has a tunable decay.
export type AudioSource = 'electro' | 'organic' | '808';

// Humanization knobs for organic (sample) mode. Drum hits get a small ±
// timing jitter and a velocity wobble so a held loop doesn't read as
// "machine-quantized."
const JITTER_MS = 12;       // ±12 ms per hit
const VELOCITY_WOBBLE = 0.15; // ± up to 15% of nominal velocity
function humanize(time: number, velocity: number): { time: number; velocity: number } {
  const jitter = (Math.random() * 2 - 1) * (JITTER_MS / 1000);
  const wobble = 1 - Math.random() * VELOCITY_WOBBLE; // bias slightly down
  return { time: Math.max(0, time + jitter), velocity: velocity * wobble };
}

export type Voice = {
  trigger: (time: number, velocity?: number, pitch?: number, opts?: TriggerOpts) => void;
  setVolume: (v: number) => void;
  // 808 kick only: retune the amplitude-envelope decay (seconds) live. The
  // iconic long-tail 808 boom comes from a high value here.
  setDecay?: (seconds: number) => void;
  // Pitched (electro) voices: portamento time in seconds. With glide > 0 each
  // note onset slides its pitch in from the previous note instead of snapping.
  // 0 = instant pitch snap. Pluck (Karplus-Strong) and the sampled tones have
  // no held oscillator to bend, so they don't implement this.
  setGlide?: (seconds: number) => void;
  release?: () => void;
  dispose?: () => void;
  // Internal handle to the voice's terminal node. Not accessed outside
  // audio.ts; optional so deferred-voice wrappers can omit it pre-init.
  output?: Tone.ToneAudioNode;
};

// Timbre selection for the tonal track. Each id maps to a voice factory below.
export type ToneTimbre = 'synth' | 'piano' | 'bass' | 'bell' | 'organ' | 'pluck' | 'voice';

export const TONE_TIMBRES: { id: ToneTimbre; label: string }[] = [
  { id: 'synth', label: 'Synth' },
  { id: 'piano', label: 'Piano' },
  { id: 'bass',  label: 'Bass' },
  { id: 'bell',  label: 'Bell' },
  { id: 'organ', label: 'Organ' },
  { id: 'pluck', label: 'Pluck' },
  { id: 'voice', label: 'Voice' },
];

// Tone-track glide (portamento). The UI slider runs 0..100; that maps onto a
// portamento time of 0..MAX_GLIDE_SEC seconds applied on every note onset.
// 0 = pizzicato (each step snaps to pitch); 100 = a long slur into each note.
// Honored by all electro tone voices except pluck (Karplus-Strong has no held
// pitch to bend); the sampled tones snap.
export const DEFAULT_GLIDE = 20;
export const MAX_GLIDE_SEC = 0.3;
export function glideToSeconds(g: number): number {
  return (Math.max(0, Math.min(100, g)) / 100) * MAX_GLIDE_SEC;
}

// Lazy master chain. Instantiating Tone audio nodes at module load creates
// the AudioContext before the user has interacted, which both wastes
// resources and floods the console with "AudioContext was not allowed to
// start" warnings (Chrome's autoplay policy notice). We only build the chain
// when a voice actually needs to connect — and voices themselves are wrapped
// in deferred factories on the App side, so neither happens before the
// splash gesture resumes the context.
let _masterChain: Tone.ToneAudioNode | null = null;
function ensureMasterChain(): Tone.ToneAudioNode {
  if (_masterChain) return _masterChain;
  const comp = new Tone.Compressor({
    threshold: -8,
    ratio: 3,
    attack: 0.005,
    release: 0.1,
  });
  const lim = new Tone.Limiter(-1);
  const gain = new Tone.Gain(0.9);
  comp.chain(lim, gain, Tone.getDestination());
  _masterChain = comp;
  return _masterChain;
}

// Voice factories funnel through this getter so `bounceToWav()` can briefly
// swap in an offline-context master chain during a Tone.Offline render. The
// override is process-wide; we only invoke it serially from one click handler.
let _masterOverride: Tone.ToneAudioNode | null = null;
function currentMaster(): Tone.ToneAudioNode {
  return _masterOverride ?? ensureMasterChain();
}

function connect(voice: Tone.ToneAudioNode) {
  voice.connect(currentMaster());
}

// Each voice ends in a user-controlled volume Gain so the mixer sliders can
// scale the level without disturbing the internal mix balance of that voice.
function userVolume(initial = 1.0): Tone.Gain {
  return new Tone.Gain(initial);
}

// Snare / hat amplitude-envelope decay ranges (seconds). Like the 808 kick
// decay, these retune the live voice's envelope via setDecay(). Honored by the
// electro synth voices and the 808 voices; the organic sample voices have no
// envelope, so the decay slider is hidden in that engine.
//
// For electro, "decay" controls the krak length of the snare and the tick
// length of the hat — short by design, since electro's character is tight and
// mechanical (vs. the 808's longer wash). Tune/tone were considered as the
// exposed knob; decay won because it's the single most audible dimension of
// electro percussion and reuses the existing slider/hash/collab plumbing.
export const SNARE_DECAY = { min: 0.04, max: 0.5, default: 0.18 } as const;
export const HAT_DECAY = { min: 0.01, max: 0.35, default: 0.05 } as const;

// Electro kick — Detroit electro / Daft Punk techno thud. Distinct from the
// long sine-sweep 808 boom (make808Kick): a hard click transient (a 2 ms
// triangle blip), a short, punchy body with minimal pitch sweep (low octaves +
// fast pitchDecay), and a touch of waveshaper drive on the body so it cuts.
function makeKickSynth(): Voice {
  // Body: short, mostly-pitchless thud. octaves:1.6 + pitchDecay:0.012 keeps
  // the sweep tight (a quick "duff", not a falling siren), decay:0.24 is the
  // punchy-but-not-boomy tail.
  const body = new Tone.MembraneSynth({
    pitchDecay: 0.012,
    octaves: 1.6,
    envelope: { attack: 0.001, decay: 0.24, sustain: 0, release: 0.08 },
    oscillator: { type: 'sine' },
  });
  const drive = new Tone.Distortion({ distortion: 0.35, oversample: '2x', wet: 0.5 });
  const bodyGain = new Tone.Gain(0.85);
  // Click: a single high triangle ping with a near-instant envelope — the
  // attack transient that reads as "beater hitting the head."
  const click = new Tone.Synth({
    oscillator: { type: 'triangle' },
    envelope: { attack: 0.0004, decay: 0.018, sustain: 0, release: 0.005 },
  });
  const clickGain = new Tone.Gain(0.5);
  const vol = userVolume();
  body.chain(drive, bodyGain, vol);
  click.chain(clickGain, vol);
  connect(vol);

  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    dispose: () => { body.dispose(); drive.dispose(); bodyGain.dispose(); click.dispose(); clickGain.dispose(); vol.dispose(); },
    trigger: (time, velocity = 1) => {
      body.triggerAttackRelease('C1', '16n', time, velocity);
      click.triggerAttackRelease(2200, 0.018, time, velocity);
    },
  };
}

// Electro snare — a tight tonal "krak", not a wash. Drexciya / Model 500, not
// Pete Rock. Higher-pitched body (D4) with a fast decay, noise high-passed hard
// (~3 kHz) so the tail is bright/snappy, and a hair of drive on the body for
// edge. The exposed decay knob retunes the noise tail length.
function makeSnareSynth(): Voice {
  const noise = new Tone.NoiseSynth({
    noise: { type: 'white' },
    envelope: { attack: 0.0008, decay: SNARE_DECAY.default, sustain: 0, release: 0.01 },
  });
  const body = new Tone.Synth({
    oscillator: { type: 'triangle' },
    envelope: { attack: 0.0006, decay: 0.06, sustain: 0, release: 0.02 },
  });
  const bodyDrive = new Tone.Distortion({ distortion: 0.25, oversample: '2x', wet: 0.4 });
  const noiseHP = new Tone.Filter(3000, 'highpass');
  const noiseGain = new Tone.Gain(0.5);
  const bodyGain = new Tone.Gain(0.5);
  const vol = userVolume();
  noise.chain(noiseHP, noiseGain, vol);
  body.chain(bodyDrive, bodyGain, vol);
  connect(vol);

  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    setDecay: (d) => { noise.envelope.decay = Math.max(0.005, d); },
    dispose: () => { noise.dispose(); body.dispose(); bodyDrive.dispose(); noiseHP.dispose(); noiseGain.dispose(); bodyGain.dispose(); vol.dispose(); },
    trigger: (time, velocity = 1) => {
      noise.triggerAttackRelease('16n', time, velocity);
      body.triggerAttackRelease(294, 0.06, time, velocity * 0.8); // ~D4
    },
  };
}

// Electro hat — clinical, mechanical, very high. Daft Punk "Around the World"
// territory: white noise gated by a fast even envelope, high-passed brutally
// (~10 kHz) with a metallic ring on top from a narrow bandpass. The decay knob
// sets the tick length (closed → barely-open).
function makeHatSynth(): Voice {
  const noise = new Tone.NoiseSynth({
    noise: { type: 'white' },
    envelope: { attack: 0.0004, decay: HAT_DECAY.default, sustain: 0, release: 0.006 },
  });
  const hp = new Tone.Filter(10000, 'highpass');
  // Narrow high bandpass adds a fixed metallic "ting" — the mechanical, almost
  // digital character vs. the 808 hat's six-oscillator clang.
  const ring = new Tone.Filter({ frequency: 13000, type: 'bandpass', Q: 3.5 });
  const ringGain = new Tone.Gain(0.6);
  const hpGain = new Tone.Gain(0.4);
  const vol = userVolume();
  noise.connect(hp);
  hp.connect(hpGain); hpGain.connect(vol);
  hp.connect(ring); ring.chain(ringGain, vol);
  connect(vol);

  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    setDecay: (d) => { noise.envelope.decay = Math.max(0.005, d); },
    dispose: () => { noise.dispose(); hp.dispose(); ring.dispose(); ringGain.dispose(); hpGain.dispose(); vol.dispose(); },
    trigger: (time, velocity = 1) => {
      noise.triggerAttackRelease('32n', time, velocity);
    },
  };
}

// Electro clap — a single tight crack. Less room, less shimmer than the 808
// clap's four-burst-plus-tail wash: one short noise burst (with one near-instant
// pre-tick for the "hands meeting" snap) through a higher, narrower bandpass.
function makeClapSynth(): Voice {
  const noise = new Tone.NoiseSynth({
    noise: { type: 'white' },
    envelope: { attack: 0.0006, decay: 0.085, sustain: 0, release: 0.01 },
  });
  const bp = new Tone.Filter({ frequency: 1700, type: 'bandpass', Q: 2.2 });
  const gain = new Tone.Gain(0.6);
  const vol = userVolume();
  noise.chain(bp, gain, vol);
  connect(vol);

  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    dispose: () => { noise.dispose(); bp.dispose(); gain.dispose(); vol.dispose(); },
    trigger: (time, velocity = 1) => {
      noise.triggerAttackRelease(0.006, time, velocity * 0.7);
      noise.triggerAttackRelease(0.085, time + 0.007, velocity);
    },
  };
}

// Tonal voice dispatcher. The timbre id selects one of the voice factories
// below; each implements the same trigger interface so the sequencer is
// timbre-agnostic.
export function makeTone(timbre: ToneTimbre = 'synth', source: AudioSource = 'electro'): Voice {
  if (source === 'organic') return makeSampledTone(timbre);
  switch (timbre) {
    case 'piano': return makePiano();
    case 'bass':  return makeBass();
    case 'bell':  return makeBell();
    case 'organ': return makeOrgan();
    case 'pluck': return makePluck();
    case 'voice': return makeVoice();
    case 'synth':
    default:      return makeFMSynth();
  }
}

// ==== Sample-mode voices ====
//
// Drums: Tone.Sampler with a single one-shot mapped to a single key, so
// triggerAttack at that key plays the sample at native pitch.
//   - Kick/snare/hat from Tone.js audio CDN (breakbeat13 kit).
//   - Clap from tidalcycles/dirt-samples via jsdelivr — Tone.js drum CDN
//     ships no clap in any of its 14 kits, so we pull a real recorded clap
//     from the Strudel/Tidal sample bank instead.
// Tonal: each timbre gets its own real-instrument sampler from
// nbrosowsky/tonejs-instruments (bass-electric, guitar-electric, organ,
// xylophone) plus Salamander piano for piano/synth. Sampler interpolates
// between sampled notes for missing pitches.
//
// All samplers are instantiated lazily on first use of sample mode and cached
// at module scope so a SYNTH→SAMPLE→SYNTH round-trip doesn't redownload.

const TONE_AUDIO_BASE = 'https://tonejs.github.io/audio';
const NB_INSTR_BASE = 'https://nbrosowsky.github.io/tonejs-instruments/samples';
const DIRT_BASE = 'https://cdn.jsdelivr.net/gh/tidalcycles/dirt-samples@master';
const DRUM_KIT = 'breakbeat13'; // kit with the punchiest kick/snare/hat trio
const SAMPLE_KEY = 'C3';        // the synthetic key we map every drum sample to

let _kickSampler: Tone.Sampler | null = null;
let _snareSampler: Tone.Sampler | null = null;
let _hatSampler: Tone.Sampler | null = null;
let _clapSampler: Tone.Sampler | null = null;
let _pianoSampler: Tone.Sampler | null = null;
let _bassSampler: Tone.Sampler | null = null;
let _organSampler: Tone.Sampler | null = null;
let _guitarSampler: Tone.Sampler | null = null;
let _xyloSampler: Tone.Sampler | null = null;

function getKickSampler(): Tone.Sampler {
  if (!_kickSampler) {
    _kickSampler = new Tone.Sampler({
      urls: { [SAMPLE_KEY]: 'kick.mp3' },
      baseUrl: `${TONE_AUDIO_BASE}/drum-samples/${DRUM_KIT}/`,
    });
  }
  return _kickSampler;
}
function getSnareSampler(): Tone.Sampler {
  if (!_snareSampler) {
    _snareSampler = new Tone.Sampler({
      urls: { [SAMPLE_KEY]: 'snare.mp3' },
      baseUrl: `${TONE_AUDIO_BASE}/drum-samples/${DRUM_KIT}/`,
    });
  }
  return _snareSampler;
}
function getHatSampler(): Tone.Sampler {
  if (!_hatSampler) {
    _hatSampler = new Tone.Sampler({
      urls: { [SAMPLE_KEY]: 'hihat.mp3' },
      baseUrl: `${TONE_AUDIO_BASE}/drum-samples/${DRUM_KIT}/`,
    });
  }
  return _hatSampler;
}
function getClapSampler(): Tone.Sampler {
  if (!_clapSampler) {
    // dirt-samples HANDCLP0.wav — a single-shot recorded handclap from the
    // Tidal/Strudel sample bank. WAV is uncompressed so this is ~40 kB.
    _clapSampler = new Tone.Sampler({
      urls: { [SAMPLE_KEY]: 'HANDCLP0.wav' },
      baseUrl: `${DIRT_BASE}/cp/`,
    });
  }
  return _clapSampler;
}
function getPianoSampler(): Tone.Sampler {
  if (!_pianoSampler) {
    _pianoSampler = new Tone.Sampler({
      urls: {
        A0: 'A0.mp3', C1: 'C1.mp3', 'D#1': 'Ds1.mp3', 'F#1': 'Fs1.mp3',
        A1: 'A1.mp3', C2: 'C2.mp3', 'D#2': 'Ds2.mp3', 'F#2': 'Fs2.mp3',
        A2: 'A2.mp3', C3: 'C3.mp3', 'D#3': 'Ds3.mp3', 'F#3': 'Fs3.mp3',
        A3: 'A3.mp3', C4: 'C4.mp3', 'D#4': 'Ds4.mp3', 'F#4': 'Fs4.mp3',
        A4: 'A4.mp3', C5: 'C5.mp3', 'D#5': 'Ds5.mp3', 'F#5': 'Fs5.mp3',
        A5: 'A5.mp3', C6: 'C6.mp3',
      },
      baseUrl: `${TONE_AUDIO_BASE}/salamander/`,
      release: 1.2,
    });
  }
  return _pianoSampler;
}
function getBassSampler(): Tone.Sampler {
  if (!_bassSampler) {
    // Real electric bass: nbrosowsky tonejs-instruments. 17 samples across
    // E1..C#5. Names use 's' for sharp (As1.mp3 = A#1).
    _bassSampler = new Tone.Sampler({
      urls: {
        E1: 'E1.mp3', G1: 'G1.mp3', 'A#1': 'As1.mp3', 'C#2': 'Cs2.mp3',
        E2: 'E2.mp3', G2: 'G2.mp3', 'A#2': 'As2.mp3', 'C#3': 'Cs3.mp3',
        E3: 'E3.mp3', G3: 'G3.mp3', 'A#3': 'As3.mp3', 'C#4': 'Cs4.mp3',
        E4: 'E4.mp3', G4: 'G4.mp3', 'A#4': 'As4.mp3', 'C#5': 'Cs5.mp3',
      },
      baseUrl: `${NB_INSTR_BASE}/bass-electric/`,
      release: 0.6,
    });
  }
  return _bassSampler;
}
function getOrganSampler(): Tone.Sampler {
  if (!_organSampler) {
    // Real organ (Hammond-style). 21 samples across A1..A5.
    _organSampler = new Tone.Sampler({
      urls: {
        C1: 'C1.mp3', 'D#1': 'Ds1.mp3', 'F#1': 'Fs1.mp3', A1: 'A1.mp3',
        C2: 'C2.mp3', 'D#2': 'Ds2.mp3', 'F#2': 'Fs2.mp3', A2: 'A2.mp3',
        C3: 'C3.mp3', 'D#3': 'Ds3.mp3', 'F#3': 'Fs3.mp3', A3: 'A3.mp3',
        C4: 'C4.mp3', 'D#4': 'Ds4.mp3', 'F#4': 'Fs4.mp3', A4: 'A4.mp3',
        C5: 'C5.mp3', 'D#5': 'Ds5.mp3', 'F#5': 'Fs5.mp3', A5: 'A5.mp3',
        C6: 'C6.mp3',
      },
      baseUrl: `${NB_INSTR_BASE}/organ/`,
      release: 0.4,
    });
  }
  return _organSampler;
}
function getGuitarSampler(): Tone.Sampler {
  if (!_guitarSampler) {
    // Real electric guitar (clean). 17 samples across C#2..A5 — good range
    // for the pluck voice's pitched lines.
    _guitarSampler = new Tone.Sampler({
      urls: {
        'C#2': 'Cs2.mp3', E2: 'E2.mp3', 'F#2': 'Fs2.mp3',
        A2: 'A2.mp3', C3: 'C3.mp3', 'D#3': 'Ds3.mp3', 'F#3': 'Fs3.mp3',
        A3: 'A3.mp3', C4: 'C4.mp3', 'D#4': 'Ds4.mp3', 'F#4': 'Fs4.mp3',
        A4: 'A4.mp3', C5: 'C5.mp3', 'D#5': 'Ds5.mp3', 'F#5': 'Fs5.mp3',
        A5: 'A5.mp3', C6: 'C6.mp3',
      },
      baseUrl: `${NB_INSTR_BASE}/guitar-electric/`,
      release: 0.6,
    });
  }
  return _guitarSampler;
}
function getXyloSampler(): Tone.Sampler {
  if (!_xyloSampler) {
    // Xylophone for the bell voice. Only 8 samples (G4..C8) — sounds best in
    // its sampled range; we shift pitch up to use it as a bell timbre.
    _xyloSampler = new Tone.Sampler({
      urls: {
        G4: 'G4.mp3', C5: 'C5.mp3', G5: 'G5.mp3', C6: 'C6.mp3',
        G6: 'G6.mp3', C7: 'C7.mp3', G7: 'G7.mp3', C8: 'C8.mp3',
      },
      baseUrl: `${NB_INSTR_BASE}/xylophone/`,
      release: 1.0,
    });
  }
  return _xyloSampler;
}

// Cheap helper: spin up a per-track gain + connect the shared sampler
// through it so the user's volume slider scales just this track, not the
// whole sampler. The sampler is connected fresh each time a voice is built;
// disconnecting on dispose keeps the graph tidy on rapid source swaps.
//
// Critical: cleanup must call `sampler.disconnect(firstNode)` with the
// specific destination — bare `disconnect()` removes ALL consumers, which
// strands audio when a sibling voice or the new post-swap voice is mid-play.
function wireSampler(sampler: Tone.Sampler, postChain: Tone.ToneAudioNode[] = []): { vol: Tone.Gain; cleanup: () => void } {
  const vol = userVolume();
  const firstNode: Tone.ToneAudioNode = postChain[0] ?? vol;
  // Build chain: sampler → post[0] → post[1] → ... → vol → master
  let head: Tone.ToneAudioNode = sampler;
  for (const node of [...postChain, vol]) {
    head.connect(node);
    head = node;
  }
  connect(vol);
  return {
    vol,
    cleanup: () => {
      try { sampler.disconnect(firstNode); } catch { /* already disconnected */ }
      for (const node of postChain) {
        try { node.dispose(); } catch { /* noop */ }
      }
      try { vol.dispose(); } catch { /* noop */ }
    },
  };
}

export function makeKickSample(): Voice {
  const sampler = getKickSampler();
  const { vol, cleanup } = wireSampler(sampler);
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    dispose: cleanup,
    trigger: (time, velocity = 1) => {
      const h = humanize(time, velocity);
      sampler.triggerAttack(SAMPLE_KEY, h.time, h.velocity);
    },
  };
}

export function makeSnareSample(): Voice {
  const sampler = getSnareSampler();
  const { vol, cleanup } = wireSampler(sampler);
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    dispose: cleanup,
    trigger: (time, velocity = 1) => {
      const h = humanize(time, velocity);
      sampler.triggerAttack(SAMPLE_KEY, h.time, h.velocity);
    },
  };
}

export function makeHatSample(): Voice {
  const sampler = getHatSampler();
  const { vol, cleanup } = wireSampler(sampler);
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    dispose: cleanup,
    trigger: (time, velocity = 1) => {
      const h = humanize(time, velocity);
      sampler.triggerAttack(SAMPLE_KEY, h.time, h.velocity);
    },
  };
}

// Clap: real handclap one-shot from tidalcycles/dirt-samples (HANDCLP0.wav).
// Same humanization as the other drum samples so a held loop doesn't feel
// machine-quantized.
export function makeClapSample(): Voice {
  const sampler = getClapSampler();
  const { vol, cleanup } = wireSampler(sampler);
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    dispose: cleanup,
    trigger: (time, velocity = 1) => {
      const h = humanize(time, velocity);
      sampler.triggerAttack(SAMPLE_KEY, h.time, h.velocity);
    },
  };
}

// Per-timbre sampler config. Each tonal voice pill maps to a real-instrument
// sampler with its own pitch range and octave offset so it lands in its
// natural register regardless of the user's chosen scale root.
type SampledToneConfig = {
  sampler: () => Tone.Sampler;
  pitchOffset: number; // semitones; sequencer pitch is shifted before sampling
  minMidi: number;     // clamp to the sampler's actual coverage range
  maxMidi: number;
  releaseFallback: number; // step duration if releaseAt is unset (seconds)
  postChain?: () => Tone.ToneAudioNode[]; // optional per-voice filter
  // Per-voice trigger offset (seconds). Negative values fire the sample early
  // so its perceived hit lands on the beat — useful for samples with a slow
  // natural attack (organ recording fades in ~30 ms before it sounds present).
  triggerOffset?: number;
};

const SAMPLED_TONE_CONFIGS: Record<ToneTimbre, SampledToneConfig> = {
  // Synth in sample mode: piano with a darker lowpass for a "pad-like" feel.
  // We deliberately route through the piano sampler since nb's instrument
  // bank has no obvious "synth" sample.
  synth: {
    sampler: getPianoSampler,
    pitchOffset: 0,
    minMidi: 21, maxMidi: 96,
    releaseFallback: 0.5,
    postChain: () => [new Tone.Filter({ frequency: 2200, type: 'lowpass', Q: 1.0 })],
  },
  piano: {
    sampler: getPianoSampler,
    pitchOffset: 0,
    minMidi: 21, maxMidi: 96,
    releaseFallback: 0.5,
  },
  bass: {
    sampler: getBassSampler,
    pitchOffset: -12, // drop an octave so it sits in the bass register
    minMidi: 28, maxMidi: 73, // E1..C#5
    releaseFallback: 0.4,
  },
  bell: {
    sampler: getXyloSampler,
    pitchOffset: 24, // xylophone is sampled high — bump pitched lines up to land in range
    minMidi: 67, maxMidi: 108, // G4..C8
    releaseFallback: 0.8,
  },
  organ: {
    sampler: getOrganSampler,
    pitchOffset: 0,
    minMidi: 24, maxMidi: 84, // C1..C6
    releaseFallback: 0.3,
    // Organ sample has a soft natural onset; fire 80 ms early so the
    // perceived hit lands on the beat instead of feeling lazy / off-beat.
    triggerOffset: -0.080,
  },
  pluck: {
    sampler: getGuitarSampler,
    pitchOffset: 0,
    minMidi: 37, maxMidi: 81, // C#2..A5
    releaseFallback: 0.5,
  },
  // Voice has no vocal sample in nb's bank. Swap callers auto-fall back to
  // piano when source=organic so this config is never actually consumed; it
  // exists only to satisfy the `Record<ToneTimbre, ...>` type.
  voice: {
    sampler: getPianoSampler,
    pitchOffset: 0,
    minMidi: 21, maxMidi: 96,
    releaseFallback: 0.5,
  },
};

// Tonal sample voices: each timbre routes to its own real-instrument sampler.
// Pitch is the MIDI number from the sequencer; we apply the per-voice octave
// offset and clamp into the sampler's coverage before triggering.
function makeSampledTone(timbre: ToneTimbre): Voice {
  const cfg = SAMPLED_TONE_CONFIGS[timbre] ?? SAMPLED_TONE_CONFIGS.piano;
  const sampler = cfg.sampler();
  const postChain = cfg.postChain ? cfg.postChain() : [];
  const { vol, cleanup } = wireSampler(sampler, postChain);
  // Limit polyphony — release lingering notes on swap so dispose doesn't strand sound.
  let lastNote: string | null = null;
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    release: () => { if (lastNote) sampler.triggerRelease(lastNote, Tone.now()); lastNote = null; },
    dispose: cleanup,
    trigger: (time, velocity = 1, pitch = 48, opts = {}) => {
      const h = humanize(time, velocity);
      const midi = Math.max(cfg.minMidi, Math.min(cfg.maxMidi, pitch + cfg.pitchOffset));
      const note = Tone.Frequency(midi, 'midi').toNote();
      // Shift the trigger time per-voice. Negative offset for slow-attack
      // samples (organ) — pulls the perceived onset onto the beat. We clamp
      // to >=0 so we never schedule in the past relative to AudioContext.
      const offset = cfg.triggerOffset ?? 0;
      const fireAt = Math.max(0, h.time + offset);
      if (lastNote) sampler.triggerRelease(lastNote, fireAt);
      const dur = opts.releaseAt !== undefined ? Math.max(0.05, opts.releaseAt - time) : cfg.releaseFallback;
      sampler.triggerAttackRelease(note, dur, fireAt, h.velocity);
      lastNote = note;
    },
  };
}

// Wrappers for the drum factories so the sequencer's callsite is uniform:
// `makeKick(source)` returns the right voice for the current engine. The 808
// engine uses its own dedicated factories (make808Kick etc.), so it's only
// ever 'electro' (synth) or 'organic' (sample) here.
export function makeKick(source: AudioSource = 'electro'): Voice { return source === 'organic' ? makeKickSample() : makeKickSynth(); }
export function makeSnare(source: AudioSource = 'electro'): Voice { return source === 'organic' ? makeSnareSample() : makeSnareSynth(); }
export function makeHat(source: AudioSource = 'electro'): Voice { return source === 'organic' ? makeHatSample() : makeHatSynth(); }
export function makeClap(source: AudioSource = 'electro'): Voice { return source === 'organic' ? makeClapSample() : makeClapSynth(); }

// ==== TR-808 drum kit (fully synthesized) ====
//
// Eight classic 808 voices. Each is a small Web-Audio recipe — no samples.
// The kit gets its own track layout in the UI (no tonal track); see main.ts.
//
// Master-chain note: every voice ends in a userVolume() Gain and connect()s
// to currentMaster(), same as the other synth voices, so the mixer sliders
// and the offline-bounce override work unchanged.

export const DEFAULT_808_KICK_DECAY = 0.55; // seconds — the tunable 808 boom
export const MIN_808_KICK_DECAY = 0.12;
export const MAX_808_KICK_DECAY = 1.4;

// 808 kick: sine body with a fast downward pitch sweep (the "click"), feeding
// an amplitude envelope whose decay is the iconic tunable parameter. Built on
// MembraneSynth so the pitch-drop + amp envelope come for free.
export function make808Kick(decay: number = DEFAULT_808_KICK_DECAY): Voice {
  const clamp = (d: number) => Math.max(MIN_808_KICK_DECAY, Math.min(MAX_808_KICK_DECAY, d));
  let dec = clamp(decay);
  const synth = new Tone.MembraneSynth({
    pitchDecay: 0.05,
    octaves: 6,
    envelope: { attack: 0.001, decay: dec, sustain: 0, release: 0.04 },
    oscillator: { type: 'sine' },
  });
  const gain = new Tone.Gain(1.0);
  const vol = userVolume();
  synth.chain(gain, vol);
  connect(vol);
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    setDecay: (d) => { dec = clamp(d); synth.envelope.decay = dec; },
    dispose: () => { synth.dispose(); gain.dispose(); vol.dispose(); },
    trigger: (time, velocity = 1) => {
      synth.triggerAttackRelease('C1', dec + 0.05, time, velocity);
    },
  };
}

// 808 snare: white-noise "snares" through a highpass, plus two short tonal
// partials (the drum shell) — the canonical 808 snare voicing.
export function make808Snare(): Voice {
  const noise = new Tone.NoiseSynth({
    noise: { type: 'white' },
    envelope: { attack: 0.001, decay: SNARE_DECAY.default, sustain: 0, release: 0.02 },
  });
  const noiseHP = new Tone.Filter(1700, 'highpass');
  const noiseGain = new Tone.Gain(0.5);
  const lo = new Tone.Synth({ oscillator: { type: 'triangle' }, envelope: { attack: 0.001, decay: 0.11, sustain: 0, release: 0.02 } });
  const hi = new Tone.Synth({ oscillator: { type: 'triangle' }, envelope: { attack: 0.001, decay: 0.09, sustain: 0, release: 0.02 } });
  const toneGain = new Tone.Gain(0.4);
  const vol = userVolume();
  noise.chain(noiseHP, noiseGain, vol);
  lo.connect(toneGain); hi.connect(toneGain); toneGain.connect(vol);
  connect(vol);
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    setDecay: (d) => { noise.envelope.decay = Math.max(0.005, d); },
    dispose: () => { noise.dispose(); noiseHP.dispose(); noiseGain.dispose(); lo.dispose(); hi.dispose(); toneGain.dispose(); vol.dispose(); },
    trigger: (time, velocity = 1) => {
      noise.triggerAttackRelease('16n', time, velocity);
      lo.triggerAttackRelease(185, '16n', time, velocity * 0.9);
      hi.triggerAttackRelease(330, '16n', time, velocity * 0.6);
    },
  };
}

// 808 clap: four quick noise bursts in rapid succession + a longer noise
// "room" tail, all band-passed around 1 kHz for that hand-clap snap.
export function make808Clap(): Voice {
  const noise = new Tone.NoiseSynth({ noise: { type: 'white' }, envelope: { attack: 0.001, decay: 0.012, sustain: 0, release: 0.005 } });
  const tail = new Tone.NoiseSynth({ noise: { type: 'white' }, envelope: { attack: 0.002, decay: 0.18, sustain: 0, release: 0.02 } });
  const bp = new Tone.Filter({ frequency: 1050, type: 'bandpass', Q: 1.2 });
  const tailGain = new Tone.Gain(0.35);
  const vol = userVolume();
  noise.connect(bp); tail.chain(tailGain, bp); bp.connect(vol);
  connect(vol);
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    dispose: () => { noise.dispose(); tail.dispose(); bp.dispose(); tailGain.dispose(); vol.dispose(); },
    trigger: (time, velocity = 1) => {
      noise.triggerAttackRelease(0.012, time, velocity);
      noise.triggerAttackRelease(0.012, time + 0.010, velocity * 0.9);
      noise.triggerAttackRelease(0.012, time + 0.020, velocity * 0.85);
      noise.triggerAttackRelease(0.012, time + 0.030, velocity * 0.75);
      tail.triggerAttackRelease(0.18, time + 0.030, velocity * 0.7);
    },
  };
}

// 808 hats: a fixed bank of six square-wave oscillators at the 808's
// inharmonic frequencies. Their upper harmonics, high-passed hard, give the
// metallic hiss; an amplitude envelope sets open vs. closed length.
const HAT_808_FREQS = [205.3, 304.4, 369.6, 522.7, 540, 800];
function makeMetallicHat(decay: number): Voice {
  let dec = decay;
  const oscs = HAT_808_FREQS.map((f) => new Tone.Oscillator({ frequency: f, type: 'square' }).start());
  // The hard 7.2k highpass + 10.5k bandpass throw away most of the square-wave
  // energy, so this pre-filter mix sits much hotter than its raw 6-osc sum
  // would suggest — tuned so the 808 hat sits level with the kick/snare/clap
  // at the same volume-slider position.
  const mix = new Tone.Gain(0.4);
  oscs.forEach((o) => o.connect(mix));
  const hp = new Tone.Filter(7200, 'highpass');
  const bp = new Tone.Filter({ frequency: 10500, type: 'bandpass', Q: 1.4 });
  const ampEnv = new Tone.AmplitudeEnvelope({ attack: 0.001, decay: dec, sustain: 0, release: 0.02 });
  const vol = userVolume();
  mix.chain(hp, bp, ampEnv, vol);
  connect(vol);
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    setDecay: (d) => { dec = Math.max(0.005, d); ampEnv.decay = dec; },
    dispose: () => { oscs.forEach((o) => o.dispose()); mix.dispose(); hp.dispose(); bp.dispose(); ampEnv.dispose(); vol.dispose(); },
    trigger: (time, velocity = 1) => { ampEnv.triggerAttackRelease(dec, time, velocity); },
  };
}
export function make808ClosedHat(): Voice { return makeMetallicHat(0.05); }
export function make808OpenHat(): Voice { return makeMetallicHat(0.4); }

// 808 cowbell: two square-wave oscillators a fixed inharmonic interval apart
// (the 540 Hz / 800 Hz pair from the hat bank), through a bandpass — the
// unmistakable "more cowbell" clang.
export function make808Cowbell(): Voice {
  const o1 = new Tone.Oscillator({ frequency: 540, type: 'square' }).start();
  const o2 = new Tone.Oscillator({ frequency: 800, type: 'square' }).start();
  const mix = new Tone.Gain(0.18);
  o1.connect(mix); o2.connect(mix);
  const bp = new Tone.Filter({ frequency: 2640, type: 'bandpass', Q: 1.2 });
  const ampEnv = new Tone.AmplitudeEnvelope({ attack: 0.001, decay: 0.12, sustain: 0.06, release: 0.3 });
  const vol = userVolume();
  mix.chain(bp, ampEnv, vol);
  connect(vol);
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    dispose: () => { o1.dispose(); o2.dispose(); mix.dispose(); bp.dispose(); ampEnv.dispose(); vol.dispose(); },
    trigger: (time, velocity = 1) => { ampEnv.triggerAttackRelease(0.22, time, velocity); },
  };
}

// 808 tom: a sine body with a downward pitch sweep — the kick recipe, tuned
// up an octave-and-change and given a shorter tail. (Single mid tom; the 808
// had low/mid/hi — one is plenty for a 16-step grid.)
export function make808Tom(): Voice {
  const synth = new Tone.MembraneSynth({
    pitchDecay: 0.06,
    octaves: 3,
    envelope: { attack: 0.001, decay: 0.32, sustain: 0, release: 0.05 },
    oscillator: { type: 'sine' },
  });
  const vol = userVolume();
  synth.chain(vol);
  connect(vol);
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    dispose: () => { synth.dispose(); vol.dispose(); },
    trigger: (time, velocity = 1) => { synth.triggerAttackRelease('A2', '8n', time, velocity); },
  };
}

// 808 rim (rimshot): a very short tonal click — a narrow-Q band of a square
// oscillator with a near-instant envelope. Snappy and dry.
export function make808Rim(): Voice {
  const osc = new Tone.Oscillator({ frequency: 1700, type: 'square' }).start();
  const bp = new Tone.Filter({ frequency: 1700, type: 'bandpass', Q: 7 });
  const ampEnv = new Tone.AmplitudeEnvelope({ attack: 0.0005, decay: 0.028, sustain: 0, release: 0.01 });
  const vol = userVolume(1.2);
  osc.chain(bp, ampEnv, vol);
  connect(vol);
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    dispose: () => { osc.dispose(); bp.dispose(); ampEnv.dispose(); vol.dispose(); },
    trigger: (time, velocity = 1) => { ampEnv.triggerAttackRelease(0.028, time, velocity); },
  };
}

// Returns true once every sample mode buffer has finished loading. The toggle
// disables itself until this resolves so the user can't hot-swap into a
// silent engine.
export function ensureSamplesLoaded(): Promise<void> {
  // Touch each sampler so the constructors fire and start fetching.
  getKickSampler(); getSnareSampler(); getHatSampler(); getClapSampler();
  getPianoSampler(); getBassSampler(); getOrganSampler();
  getGuitarSampler(); getXyloSampler();
  return Tone.loaded();
}

// Trigger / release controller shared by the held-and-legato electro tonal
// voices (the "Synth" FMSynth and the "Bass" MonoSynth). Both want the same
// articulation:
//   - consecutive on-steps glide the pitch without re-firing the envelope,
//   - a run that ends on an off-step releases at the step boundary,
//   - a fully-filled tone row drones (no scheduled release ever).
//
// The important design choice: this never calls the synth's `triggerRelease()`.
// In Tone.js, `Monophonic.triggerRelease()` does `oscillator.stop(t + release)`
// — and if the note is re-attacked before that scheduled stop fires (e.g. at
// the loop boundary of an all-on row, or right after a one-step gap), the stop
// still runs and the voice goes permanently silent. That race was the root of
// the long-running "full tone row goes silent" bug and several brittle patches
// around it. By starting the oscillators on the first attack and then only ever
// driving the *envelopes* — `synth.triggerAttack()` re-fires them; we release
// them directly — there is no oscillator stop to lose the race to.
function makeHeldElectroController(
  synth: {
    frequency: { rampTo: (value: number, rampTime: number, startTime: number) => void };
    portamento: number;
    triggerAttack: (note: string, time: number, velocity: number) => void;
  },
  // The envelope(s) to release directly. For FMSynth: [envelope,
  // modulationEnvelope]; for MonoSynth: [envelope, filterEnvelope].
  releaseEnvelopes: Tone.Envelope[],
): Pick<Voice, 'setGlide' | 'release' | 'trigger'> {
  let glideSec = glideToSeconds(DEFAULT_GLIDE);
  let everAttacked = false;
  // AudioContext time the current note ends: -1 = released / idle (next onset
  // must re-attack), Infinity = held with no scheduled end (drone), otherwise
  // the time triggerRelease was scheduled for.
  let releaseTime = -1;
  const releaseNow = (when: number) => { for (const e of releaseEnvelopes) e.triggerRelease(when); };
  return {
    setGlide: (s) => { glideSec = Math.max(0, s); },
    release: () => { releaseNow(Tone.now()); releaseTime = -1; },
    trigger: (time, velocity = 1, pitch = 48, opts = {}) => {
      const held = everAttacked && time < releaseTime;
      if (opts.legatoFromPrev && held) {
        // The voice is still sounding, so glide rather than re-attack. But if a
        // release was already scheduled (finite releaseTime — e.g. a real-time
        // pitch/toggle preview fired `releaseNow(now + 0.15)` just before the
        // row was filled) and this onset now extends the hold indefinitely,
        // that pending release would still drop the envelope to 0 mid-drone and
        // never recover (every following step keeps gliding). Cancel it so the
        // drone actually sustains. This is the "full tone row goes silent when
        // the last pad is enabled" bug.
        if (opts.releaseAt === undefined && releaseTime !== Infinity) {
          for (const e of releaseEnvelopes) e.cancel(Tone.now());
        }
        synth.frequency.rampTo(Tone.Frequency(pitch, 'midi').toFrequency(), glideSec, time);
      } else {
        // Cancel any pending envelope release before re-attacking. The previous
        // step may have scheduled envelope.triggerRelease(time - 0.001). Tone.js's
        // triggerAttack only calls cancelScheduledValues(time), which misses that
        // event (it's 1ms earlier). We cancel from Tone.now() (current real-time)
        // which sweeps all look-ahead-buffered future events including that release.
        if (everAttacked) {
          for (const e of releaseEnvelopes) e.cancel(Tone.now());
        }
        everAttacked = true;
        synth.portamento = glideSec;
        synth.triggerAttack(Tone.Frequency(pitch, 'midi').toNote(), time, velocity);
      }
      if (opts.releaseAt !== undefined) {
        releaseNow(opts.releaseAt);
        releaseTime = opts.releaseAt;
      } else {
        releaseTime = Infinity;
      }
    },
  };
}

// FM stab — original "Synth" voice. Triangle carrier through a resonant
// lowpass with FM modulation. Honors glide (portamento on every note onset;
// legato runs between consecutive on-steps avoid re-attacking the envelope).
function makeFMSynth(): Voice {
  const synth = new Tone.FMSynth({
    harmonicity: 1.5,
    modulationIndex: 8,
    oscillator: { type: 'triangle' },
    modulation: { type: 'sine' },
    envelope: { attack: 0.003, decay: 0.18, sustain: 0.4, release: 0.3 },
    modulationEnvelope: { attack: 0.008, decay: 0.16, sustain: 0.1, release: 0.2 },
    volume: 4,
    portamento: 0,
  });
  const filter = new Tone.Filter({ frequency: 2400, type: 'lowpass', Q: 2 });
  const gain = new Tone.Gain(1.6);
  const vol = userVolume(1.2);
  synth.chain(filter, gain, vol);
  connect(vol);

  const ctrl = makeHeldElectroController(synth, [synth.envelope, synth.modulationEnvelope]);
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    dispose: () => { synth.dispose(); filter.dispose(); gain.dispose(); vol.dispose(); },
    ...ctrl,
  };
}

// Piano — bright triangle with quick decay. Percussive (re-attacks every step);
// with glide > 0 the oscillator slides into each new note's pitch from the
// previous one (portamento), so the slider is audible on this timbre too.
function makePiano(): Voice {
  const synth = new Tone.Synth({
    oscillator: { type: 'triangle' },
    envelope: { attack: 0.003, decay: 0.55, sustain: 0.0, release: 0.5 },
    volume: 0,
    portamento: 0,
  });
  const filter = new Tone.Filter({ frequency: 5500, type: 'lowpass', Q: 0.6 });
  const vol = userVolume(1.0);
  synth.chain(filter, vol);
  connect(vol);
  let glideSec = glideToSeconds(DEFAULT_GLIDE);
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    setGlide: (s) => { glideSec = Math.max(0, s); },
    release: () => synth.triggerRelease(Tone.now()),
    dispose: () => { synth.dispose(); filter.dispose(); vol.dispose(); },
    trigger: (time, velocity = 1, pitch = 48) => {
      const note = Tone.Frequency(pitch, 'midi').toNote();
      synth.portamento = glideSec;
      synth.triggerAttackRelease(note, '8n', time, velocity);
    },
  };
}

// Bass — sawtooth MonoSynth with filter envelope; supports legato glide.
function makeBass(): Voice {
  const synth = new Tone.MonoSynth({
    oscillator: { type: 'sawtooth' },
    envelope: { attack: 0.005, decay: 0.18, sustain: 0.7, release: 0.2 },
    filterEnvelope: { attack: 0.005, decay: 0.12, sustain: 0.4, release: 0.2, baseFrequency: 280, octaves: 2.4 },
    volume: 0,
    portamento: 0,
  });
  const vol = userVolume(1.1);
  synth.chain(vol);
  connect(vol);
  const ctrl = makeHeldElectroController(synth, [synth.envelope, synth.filterEnvelope]);
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    dispose: () => { synth.dispose(); vol.dispose(); },
    ...ctrl,
  };
}

// Bell — high-harmonicity FM with long decay. Percussive; with glide > 0 each
// strike's pitch slides in from the previous note (portamento).
function makeBell(): Voice {
  const synth = new Tone.FMSynth({
    harmonicity: 3.5,
    modulationIndex: 14,
    oscillator: { type: 'sine' },
    modulation: { type: 'sine' },
    envelope: { attack: 0.001, decay: 1.4, sustain: 0, release: 1.6 },
    modulationEnvelope: { attack: 0.001, decay: 0.7, sustain: 0, release: 1.0 },
    volume: -2,
    portamento: 0,
  });
  const vol = userVolume(1.0);
  synth.chain(vol);
  connect(vol);
  let glideSec = glideToSeconds(DEFAULT_GLIDE);
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    setGlide: (s) => { glideSec = Math.max(0, s); },
    release: () => synth.triggerRelease(Tone.now()),
    dispose: () => { synth.dispose(); vol.dispose(); },
    trigger: (time, velocity = 1, pitch = 48) => {
      const note = Tone.Frequency(pitch, 'midi').toNote();
      synth.portamento = glideSec;
      synth.triggerAttackRelease(note, '4n', time, velocity);
    },
  };
}

// Organ — fundamental + octave-up partial for additive shimmer. Holds for the
// step duration (uses opts.releaseAt to set length when consecutive on-steps).
// With glide > 0, portamento on the PolySynth voices slides each new note's
// pitch in from the voice's previous note.
function makeOrgan(): Voice {
  const synth = new Tone.PolySynth(Tone.Synth, {
    oscillator: { type: 'sine' },
    envelope: { attack: 0.02, decay: 0.0, sustain: 1.0, release: 0.18 },
    volume: -8,
    portamento: 0,
  });
  const high = new Tone.PolySynth(Tone.Synth, {
    oscillator: { type: 'triangle' },
    envelope: { attack: 0.02, decay: 0.0, sustain: 0.6, release: 0.18 },
    volume: -16,
    portamento: 0,
  });
  const vol = userVolume(1.0);
  synth.connect(vol);
  high.connect(vol);
  connect(vol);
  let curPortamento = 0;
  const setPortamento = (p: number) => {
    if (p === curPortamento) return;
    curPortamento = p;
    synth.set({ portamento: p });
    high.set({ portamento: p });
  };
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    setGlide: (s) => setPortamento(Math.max(0, s)),
    release: () => { synth.releaseAll(); high.releaseAll(); },
    dispose: () => { synth.dispose(); high.dispose(); vol.dispose(); },
    trigger: (time, velocity = 1, pitch = 48, opts = {}) => {
      const note = Tone.Frequency(pitch, 'midi').toNote();
      const noteHigh = Tone.Frequency(pitch + 12, 'midi').toNote();
      const dur = opts.releaseAt !== undefined ? Math.max(0.05, opts.releaseAt - time) : 0.18;
      synth.triggerAttackRelease(note, dur, time, velocity);
      high.triggerAttackRelease(noteHigh, dur, time, velocity * 0.5);
    },
  };
}

// Pluck — Karplus-Strong string. Always re-attacks (no held tone or glide).
function makePluck(): Voice {
  const synth = new Tone.PluckSynth({
    attackNoise: 1,
    dampening: 4200,
    resonance: 0.85,
  });
  const vol = userVolume(1.4);
  synth.connect(vol);
  connect(vol);
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    dispose: () => { synth.dispose(); vol.dispose(); },
    trigger: (time, _velocity = 1, pitch = 48) => {
      const note = Tone.Frequency(pitch, 'midi').toNote();
      synth.triggerAttack(note, time);
    },
  };
}

// Voice — formant-filtered saw + breath layer. Sawtooth source through three
// parallel bandpass filters at "ah" vowel formants (730 / 1090 / 2440 Hz);
// the fixed formant peaks are what reads as a vocal tract. A pink-noise
// breath layer (HP'd, gated by the synth's amp envelope) adds air; a slow
// ±15-cent vibrato adds the natural pitch wobble of a singer. Legato runs
// glide via makeHeldElectroController, same as synth/bass. Dev-only timbre
// behind the bsdev flag; not surfaced in the production UI.
function makeVoice(): Voice {
  // Sawtooth source. Tone.MonoSynth's internal lowpass is wide-open so the
  // formant filters downstream are what shape the timbre.
  const synth = new Tone.MonoSynth({
    oscillator: { type: 'sawtooth' },
    envelope: { attack: 0.04, decay: 0.12, sustain: 0.85, release: 0.25 },
    filterEnvelope: { attack: 0.01, decay: 0.1, sustain: 1, release: 0.2, baseFrequency: 8000, octaves: 0 },
    filter: { type: 'lowpass', frequency: 8000, Q: 0 },
    volume: -6,
    portamento: 0,
  });
  // Clear the kick zone.
  const sourceHP = new Tone.Filter({ frequency: 180, type: 'highpass', Q: 0.7 });
  // Three parallel bandpass formants — "ah" vowel. Gains stagger the formant
  // levels (F1 strongest, F3 quietest) for a natural-sounding spectrum.
  const formantBus = new Tone.Gain(1.0);
  const f1 = new Tone.Filter({ frequency: 730,  type: 'bandpass', Q: 8 });
  const f2 = new Tone.Filter({ frequency: 1090, type: 'bandpass', Q: 10 });
  const f3 = new Tone.Filter({ frequency: 2440, type: 'bandpass', Q: 12 });
  const f1g = new Tone.Gain(1.0);
  const f2g = new Tone.Gain(0.63); // -4 dB
  const f3g = new Tone.Gain(0.4);  // -8 dB
  synth.connect(sourceHP);
  sourceHP.connect(f1); f1.chain(f1g, formantBus);
  sourceHP.connect(f2); f2.chain(f2g, formantBus);
  sourceHP.connect(f3); f3.chain(f3g, formantBus);
  // Pink-noise breath layer. HP'd hard so it sits as air on top, gated by
  // its own envelope mirrored to the synth amp envelope. We trigger this
  // envelope manually alongside the synth attack so legato glides don't
  // re-fire it (a fresh breath puff on every glide step reads as a stutter).
  const breath = new Tone.Noise('pink').start();
  const breathHP = new Tone.Filter({ frequency: 2200, type: 'highpass', Q: 0.7 });
  const breathEnv = new Tone.AmplitudeEnvelope({ attack: 0.04, decay: 0.12, sustain: 0.6, release: 0.25 });
  const breathGain = new Tone.Gain(0.12);
  breath.chain(breathHP, breathEnv, breathGain, formantBus);
  // Vibrato: ±15 cents at 5 Hz on the oscillator detune. Static (no attack
  // delay) — v1 keeps it simple; pitched controls track via synth.frequency,
  // detune is independent.
  const vibrato = new Tone.LFO({ frequency: 5, min: -15, max: 15, type: 'sine' });
  vibrato.connect(synth.detune as unknown as Tone.Param<'cents'>);
  vibrato.start();
  const vol = userVolume(0.9);
  formantBus.connect(vol);
  connect(vol);

  const ctrl = makeHeldElectroController(synth, [synth.envelope, synth.filterEnvelope]);
  // Wrap trigger so the breath envelope only fires on actual note onsets,
  // not on legato glide steps.
  const wrappedTrigger: Voice['trigger'] = (time, velocity = 1, pitch = 48, opts = {}) => {
    ctrl.trigger!(time, velocity, pitch, opts);
    if (!opts.legatoFromPrev) {
      const dur = opts.releaseAt !== undefined ? Math.max(0.05, opts.releaseAt - time) : 0.3;
      breathEnv.triggerAttackRelease(dur, time, velocity);
    } else if (opts.releaseAt !== undefined) {
      breathEnv.triggerRelease(opts.releaseAt);
    }
  };
  return {
    output: vol,
    setVolume: (v) => vol.gain.rampTo(v, 0.02),
    setGlide: ctrl.setGlide,
    release: () => { ctrl.release!(); breathEnv.triggerRelease(Tone.now()); },
    dispose: () => {
      synth.dispose(); sourceHP.dispose(); f1.dispose(); f2.dispose(); f3.dispose();
      f1g.dispose(); f2g.dispose(); f3g.dispose(); formantBus.dispose();
      breath.dispose(); breathHP.dispose(); breathEnv.dispose(); breathGain.dispose();
      vibrato.dispose(); vol.dispose();
    },
    trigger: wrappedTrigger,
  };
}

export async function startAudio(): Promise<void> {
  await Tone.start();
}

// ==== Offline render → WAV ====
// Bounces a pattern to an AudioBuffer using Tone.Offline. The voice factories
// are reused as-is; we just swap `currentMaster()` for the offline compressor
// while the render runs.
export type BounceOpts = {
  bpm: number;
  numSteps: number;
  // Per-track active step indices, in the unified 8-track layout order:
  //   kick, snare, hat, clap, tone, cowbell, tom, rim
  // The 808 engine swaps the first four (and the kick decay); the cowbell /
  // tom / rim voices are always the synthesized TR-808 ones.
  patterns: number[][];
  // Per-step MIDI pitches for the tone track (length = numSteps).
  tonePitches: number[];
  // Per-track volume multipliers, keyed by the layout's track keys.
  volumes: Record<string, number>;
  toneTimbre: ToneTimbre;
  audioSource: AudioSource;
  // 808 only: amplitude-envelope decay of the kick (seconds).
  kickDecay?: number;
  // Tone-track glide, 0..100 (see DEFAULT_GLIDE).
  glide?: number;
  // Snare / hat amplitude-envelope decay (seconds). No effect for organic.
  snareDecay?: number;
  hatDecay?: number;
  // Tone-row octave shift in semitones (multiple of 12). Added to every tone pitch.
  toneOctaveOffset?: number;
};

// Unified 8-track key order — same for every source.
const TRACK_ORDER = ['k', 's', 'h', 'c', 't', 'b', 'm', 'r'] as const;
const TONE_IDX = 4;

export async function bounceToWav(opts: BounceOpts): Promise<AudioBuffer> {
  const stepDur = 60 / opts.bpm / 4; // 16th note
  const tail = 2.2; // longest voice tail (bell release / long 808 kick)
  const totalDur = opts.numSteps * stepDur + tail;
  const is808 = opts.audioSource === '808';
  // Organic (sample) mode needs the CDN buffers in the offline context, which
  // Tone.Offline can't share. So organic loops bounce as their electro twin —
  // fine for the share-link WAV. Electro and 808 are fully synthesized and
  // render deterministically as-is.
  const source: AudioSource = is808 ? '808' : 'electro';

  const buffer = await Tone.Offline(async () => {
    const comp = new Tone.Compressor({ threshold: -8, ratio: 3, attack: 0.005, release: 0.1 });
    const lim = new Tone.Limiter(-1);
    const gain = new Tone.Gain(0.9);
    comp.chain(lim, gain, Tone.getDestination());

    _masterOverride = comp;
    try {
      const voices: Voice[] = [
        is808 ? make808Kick(opts.kickDecay) : makeKick(source),
        is808 ? make808Snare() : makeSnare(source),
        is808 ? make808ClosedHat() : makeHat(source),
        is808 ? make808Clap() : makeClap(source),
        makeTone(opts.toneTimbre, source),
        make808Cowbell(),
        make808Tom(),
        make808Rim(),
      ];
      voices.forEach((v, i) => v.setVolume(opts.volumes[TRACK_ORDER[i]] ?? 1));
      voices[TONE_IDX].setGlide?.(glideToSeconds(opts.glide ?? DEFAULT_GLIDE));
      voices[1].setDecay?.(opts.snareDecay ?? SNARE_DECAY.default);
      voices[2].setDecay?.(opts.hatDecay ?? HAT_DECAY.default);
      const octaveOffset = opts.toneOctaveOffset ?? 0;
      const toneActiveSet = new Set(opts.patterns[TONE_IDX] ?? []);

      for (let s = 0; s < opts.numSteps; s++) {
        const t = s * stepDur;
        const accent = s % 4 === 0 ? 1.0 : 0.85;
        for (let ti = 0; ti < voices.length; ti++) {
          // Cowbell / tom / rim are 808-only rows. Their pattern data remains
          // present across source switches, but a non-808 bounce must match
          // live playback and omit these hidden voices.
          if (!is808 && ti >= 5) continue;
          if (!opts.patterns[ti] || !opts.patterns[ti].includes(s)) continue;
          if (ti === TONE_IDX) {
            const pitch = (opts.tonePitches[s] ?? 48) + octaveOffset;
            const prev = (s - 1 + opts.numSteps) % opts.numSteps;
            const next = (s + 1) % opts.numSteps;
            voices[ti].trigger(t, accent, pitch, {
              legatoFromPrev: toneActiveSet.has(prev),
              releaseAt: toneActiveSet.has(next) ? undefined : t + stepDur - 0.001,
            });
          } else {
            voices[ti].trigger(t, accent);
          }
        }
      }
    } finally {
      _masterOverride = null;
    }
  }, totalDur);

  // Tone.Offline returns a Tone.ToneAudioBuffer; unwrap to native AudioBuffer.
  return (buffer as unknown as { _buffer?: AudioBuffer; get: () => AudioBuffer }).get
    ? (buffer as unknown as { get: () => AudioBuffer }).get()
    : (buffer as unknown as AudioBuffer);
}

// Encode an AudioBuffer to a 16-bit PCM WAV file as a Blob. Inline RIFF
// writer — no dependency. Supports mono + stereo.
export function audioBufferToWavBlob(buf: AudioBuffer): Blob {
  const numCh = Math.min(2, buf.numberOfChannels);
  const sampleRate = buf.sampleRate;
  const numFrames = buf.length;
  const bytesPerSample = 2;
  const blockAlign = numCh * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = numFrames * blockAlign;
  const headerSize = 44;
  const totalSize = headerSize + dataSize;
  const ab = new ArrayBuffer(totalSize);
  const view = new DataView(ab);

  let p = 0;
  const writeStr = (s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(p++, s.charCodeAt(i)); };
  const u32 = (n: number) => { view.setUint32(p, n, true); p += 4; };
  const u16 = (n: number) => { view.setUint16(p, n, true); p += 2; };

  writeStr('RIFF');
  u32(totalSize - 8);
  writeStr('WAVE');
  writeStr('fmt ');
  u32(16);              // fmt chunk size
  u16(1);               // PCM
  u16(numCh);
  u32(sampleRate);
  u32(byteRate);
  u16(blockAlign);
  u16(bytesPerSample * 8);
  writeStr('data');
  u32(dataSize);

  const channels: Float32Array[] = [];
  for (let c = 0; c < numCh; c++) channels.push(buf.getChannelData(c));
  for (let f = 0; f < numFrames; f++) {
    for (let c = 0; c < numCh; c++) {
      let s = channels[c][f];
      if (s > 1) s = 1; else if (s < -1) s = -1;
      view.setInt16(p, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      p += 2;
    }
  }
  return new Blob([ab], { type: 'audio/wav' });
}
